// Flattens Step -> Characteristic -> Method into one row per Method, with
// rowSpan counts attached at the row where each merged block starts.
// Step-level columns (No, Process Name, Machine/Tools, Control Method,
// Reaction Plan — confirmed against every step in the source Excel) span
// every row belonging to that step. Characteristic-level columns span
// every Method row under that characteristic.
function flattenSteps(steps) {
  const rows = [];
  (steps || []).forEach((step) => {
    const stepStart = rows.length;
    const chars =
      step.characteristics && step.characteristics.length
        ? step.characteristics
        : [{}];

    chars.forEach((c) => {
      const charStart = rows.length;
      const methods = c.methods && c.methods.length ? c.methods : [{}];

      methods.forEach((m) => {
        rows.push({
          step_no: step.step_no,
          step_name: step.step_name,
          machine_tools: step.machine_tools,
          control_method: step.control_method,
          reaction_plan: step.reaction_plan,
          char_no: c.char_no,
          product_characteristic: c.product_characteristic,
          process_characteristic: c.process_characteristic,
          special_char_class: c.special_char_class,
          specification_tolerance: c.specification_tolerance,
          ...m,
        });
      });

      rows[charStart].__charSpan = rows.length - charStart;
    });

    rows[stepStart].__stepSpan = rows.length - stepStart;
  });
  return rows;
}

const th = {
  padding: "8px 10px",
  fontSize: 10,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: 0.3,
  color: "var(--text2)",
  background: "var(--surface2)",
  border: "1px solid var(--border2)",
  whiteSpace: "nowrap",
  position: "sticky",
  top: 0,
};

const td = {
  padding: "8px 10px",
  fontSize: 12,
  border: "1px solid var(--border2)",
  verticalAlign: "top",
  color: "var(--text)",
  whiteSpace: "pre-wrap",
};

export default function ControlPlanSheetView({ steps }) {
  const rows = flattenSteps(steps);

  if (rows.length === 0) {
    return (
      <div
        className="card"
        style={{ textAlign: "center", color: "var(--text3)", padding: 20 }}
      >
        No process steps
      </div>
    );
  }

  return (
    <div
      style={{
        overflowX: "auto",
        border: "1px solid var(--border2)",
        borderRadius: 8,
      }}
    >
      <table
        style={{ borderCollapse: "collapse", width: "100%", minWidth: 2000 }}
      >
        <thead>
          <tr>
            <th style={{ ...th, minWidth: 60 }} rowSpan={2}>
              Part/Process No
            </th>
            <th style={{ ...th, minWidth: 160 }} rowSpan={2}>
              Process Name / Operation Description
            </th>
            <th style={{ ...th, minWidth: 150 }} rowSpan={2}>
              Machine, Device, Jig, Tools for Manufacturing
            </th>
            <th style={{ ...th, minWidth: 50 }} rowSpan={2}>
              No.
            </th>
            <th style={{ ...th, minWidth: 140 }} rowSpan={2}>
              Product
            </th>
            <th style={{ ...th, minWidth: 140 }} rowSpan={2}>
              Process
            </th>
            <th style={{ ...th, minWidth: 70 }} rowSpan={2}>
              (*) Special Char Class
            </th>
            <th style={{ ...th, minWidth: 180 }} rowSpan={2}>
              Specifications/Tolerance Product/Process
            </th>
            <th style={{ ...th, textAlign: "center" }} colSpan={3}>
              Methods
            </th>
            <th style={{ ...th, minWidth: 140 }} rowSpan={2}>
              Error Proofing
            </th>
            <th style={{ ...th, minWidth: 160 }} rowSpan={2}>
              Control Method
            </th>
            <th style={{ ...th, textAlign: "center" }} colSpan={2}>
              Controlled By
            </th>
            <th style={{ ...th, minWidth: 180 }} rowSpan={2}>
              Reaction Plan / Corrective Action
            </th>
          </tr>
          <tr>
            <th style={{ ...th, minWidth: 160, top: 33 }}>
              Technique of Evaluation/Measurement
            </th>
            <th style={{ ...th, minWidth: 60, top: 33 }}>Size</th>
            <th style={{ ...th, minWidth: 100, top: 33 }}>Frequency</th>
            <th style={{ ...th, minWidth: 30, top: 33 }}>W</th>
            <th style={{ ...th, minWidth: 30, top: 33 }}>I</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              {r.__stepSpan && (
                <td
                  style={{
                    ...td,
                    fontWeight: 700,
                    background: "var(--surface)",
                    textAlign: "center",
                  }}
                  rowSpan={r.__stepSpan}
                >
                  {r.step_no || "—"}
                </td>
              )}
              {r.__stepSpan && (
                <td
                  style={{
                    ...td,
                    fontWeight: 600,
                    background: "var(--surface)",
                  }}
                  rowSpan={r.__stepSpan}
                >
                  {r.step_name}
                </td>
              )}
              {r.__stepSpan && (
                <td style={td} rowSpan={r.__stepSpan}>
                  {r.machine_tools || "—"}
                </td>
              )}
              {r.__charSpan && (
                <td
                  style={{ ...td, textAlign: "center" }}
                  rowSpan={r.__charSpan}
                >
                  {r.char_no || "—"}
                </td>
              )}
              {r.__charSpan && (
                <td style={td} rowSpan={r.__charSpan}>
                  {r.product_characteristic || "—"}
                </td>
              )}
              {r.__charSpan && (
                <td style={td} rowSpan={r.__charSpan}>
                  {r.process_characteristic || "—"}
                </td>
              )}
              {r.__charSpan && (
                <td
                  style={{ ...td, textAlign: "center" }}
                  rowSpan={r.__charSpan}
                >
                  {r.special_char_class || "—"}
                </td>
              )}
              {r.__charSpan && (
                <td style={td} rowSpan={r.__charSpan}>
                  {r.specification_tolerance || "—"}
                </td>
              )}
              <td style={td}>{r.evaluation_technique || "—"}</td>
              <td style={{ ...td, textAlign: "center" }}>
                {r.sample_size || "—"}
              </td>
              <td style={{ ...td, textAlign: "center" }}>
                {r.sample_frequency || "—"}
              </td>
              <td style={td}>{r.error_proofing || "—"}</td>
              {r.__stepSpan && (
                <td style={td} rowSpan={r.__stepSpan}>
                  {r.control_method || "—"}
                </td>
              )}
              <td style={{ ...td, textAlign: "center" }}>
                {r.controlled_by_worker ? "✓" : ""}
              </td>
              <td style={{ ...td, textAlign: "center" }}>
                {r.controlled_by_inspector ? "✓" : ""}
              </td>
              {r.__stepSpan && (
                <td style={td} rowSpan={r.__stepSpan}>
                  {r.reaction_plan || "—"}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
